/**
 * Reading a stored file's address. A new file's name carries a version (`<name>-<8 hex>.<ext>`) and its address may
 * carry a cache version (`?v=`, `?t=`), so neither is part of what the file is called.
 */

/** The file an address names: its last segment, without a query or fragment, decoded. */
export function fileNameOf(address: string | null | undefined): string | undefined {
  if (!address) return undefined;
  const name = address.split(/[?#]/, 1)[0].split('/').pop();
  if (!name) return undefined;
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/** A file name without the version storage put in it (`photo-1a2b3c4d.png` is `photo.png`). */
export const withoutStorageVersion = (name: string): string => name.replace(/-[0-9a-f]{8}(\.[^./]+)?$/, '$1');

/**
 * How an image's thumbnail is named: made at upload beside it, the image's whole name with this on the end
 * (`photo-1a2b3c4d.png.w1152.jpg`), a JPEG at most 1152 px wide. The backend's StoragePaths.ThumbnailSuffix.
 */
export const THUMBNAIL_SUFFIX = '.w1152.jpg';

/** Whether an address, or a file's name, is an image's thumbnail (its query and fragment aside). */
export const isThumbnail = (address: string): boolean => address.split(/[?#]/, 1)[0].endsWith(THUMBNAIL_SUFFIX);

/** `text` as a regular expression that matches it literally. */
export const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The path in `bucket` that an address of one of our files names, in any form an address has taken: the CDN's
 * (`<base>/<bucket>/<path>`), Supabase's own (`/storage/v1/object/public/<bucket>/<path>`) or the backend's
 * (`/api/v1/storage-objects/<bucket>/<path>`, `/api/storage/public/<bucket>/<path>`). Null for anything else.
 */
export function objectPathOf(address: string, bucket: string): string | null {
  let pathname: string;
  try {
    pathname = new URL(address).pathname;
  } catch {
    return null;
  }
  const match = pathname.match(new RegExp(`^(?:/storage/v1/object/public|/api/v1/storage-objects|/api/storage/public)?/${escapeRegExp(bucket)}/(.+)$`));
  if (!match) return null;
  try {
    return match[1].split('/').map(decodeURIComponent).join('/');
  } catch {
    return null;
  }
}
